import { DotOutline } from "@phosphor-icons/react";
import { clsx } from "clsx";

import LoadableImage from "common/components/ui/LoadableImage";
import Skeleton from "common/components/ui/Skeleton";
import type { WorkExperience } from "common/types";

import { formatMonthAndYear, formatRange } from "../../utils";
import ExpandableText from "./ExpandableText";
import SectionContainer from "./SectionContainer";
import SectionItem from "./SectionItem";

import CompanyFallbackImage from "assets/images/profile-placeholders/company.png";

function WorkExperienceItem({
  workExperience,
  isLoading,
}: {
  workExperience: WorkExperience;
  isLoading: boolean;
}) {
  const {
    company,
    start_date: startDate,
    end_date: endDate,
    description,
    location,
    job_title: jobTitle,
  } = workExperience;
  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-x-2">
        {isLoading ? (
          <Skeleton className="size-10 rounded-2xl" />
        ) : (
          <LoadableImage
            src={company?.logo_url}
            fallbackImage={CompanyFallbackImage}
            className="rounded-2xl size-10"
          />
        )}
        <div className="grid grow items-center justify-between gap-y-0.5 [grid-template-areas:'title''dateLocation''company'] lg:items-start lg:[grid-template-areas:'title_dateLocation''company_dateLocation']">
          {isLoading ? (
            <Skeleton className="h-4 w-40 [grid-area:title]" />
          ) : (
            <span className="break-all text-sm-bold [grid-area:title]">
              {jobTitle}
            </span>
          )}
          {isLoading ? (
            <Skeleton className="h-3 w-60 [grid-area:company]" />
          ) : (
            company && (
              <span className="text-xs-medium text-black-500 [grid-area:company]">
                {company.name}
              </span>
            )
          )}
          <div className="flex flex-col [grid-area:dateLocation] lg:items-end">
            {isLoading ? (
              <Skeleton className="h-3 w-24" />
            ) : (
              startDate && (
                <span className="text-nowrap text-button-xxs text-black-500">
                  {formatRange(
                    formatMonthAndYear(startDate),
                    endDate ? formatMonthAndYear(endDate) : "Present",
                  )}
                </span>
              )
            )}
            {location && (
              <span className="text-nowrap text-button-xxs text-black-500">
                {location.name}
              </span>
            )}
          </div>
        </div>
      </div>
      {description && (
        <ExpandableText className="ml-12">{description}</ExpandableText>
      )}
    </div>
  );
}
const noCompanyKey = "nocompany";
export default function Experience({
  workExperience,
  isLoading,
}: {
  workExperience: WorkExperience[];
  isLoading: boolean;
}) {
  const groupedWorkExperience = workExperience
    ? workExperience.reduce<Record<string, WorkExperience[]>>(
        (accumulator, currentValue) => {
          const key = currentValue.company
            ? currentValue.company.name
            : noCompanyKey;
          if (!accumulator[key]) {
            accumulator[key] = [];
          }
          accumulator[key].push(currentValue);
          return accumulator;
        },
        {},
      )
    : {};

  return (
    <SectionContainer title="Experience">
      {isLoading
        ? new Array(5).fill({}).map((experience, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
            <SectionItem key={index}>
              <WorkExperienceItem
                workExperience={experience}
                isLoading={isLoading}
              />
            </SectionItem>
          ))
        : Object.entries(groupedWorkExperience).map(
            ([companyName, experiences]) => {
              if (experiences.length > 1 && companyName !== noCompanyKey) {
                const firstJobStart =
                  experiences[experiences.length - 1].start_date;
                const lastJobEnd = experiences[0].end_date;

                return (
                  <SectionItem key={companyName}>
                    <div className="mb-2.5 flex items-center">
                      <LoadableImage
                        className="mr-2 rounded-2xl size-10"
                        src={experiences[0].company.logo_url}
                        fallbackImage={CompanyFallbackImage}
                      />
                      <span className="text-sm-bold">{companyName}</span>
                      {firstJobStart && (
                        <span className="ml-auto text-xs">
                          {formatRange(
                            formatMonthAndYear(firstJobStart),
                            lastJobEnd
                              ? formatMonthAndYear(lastJobEnd)
                              : "Present",
                          )}
                        </span>
                      )}
                    </div>
                    {experiences.map(
                      (
                        {
                          job_title,
                          start_date,
                          end_date,
                          description,
                          location,
                          employment_type,
                        },
                        index,
                      ) => (
                        <div
                          className="flex items-stretch gap-x-2"
                          // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
                          key={index}
                        >
                          <div className="-mb-3 flex w-10 shrink-0 flex-col items-center pt-3">
                            <DotOutline
                              className="text-black-400"
                              size={16}
                              weight="fill"
                            />
                            {index !== experiences.length - 1 && (
                              <div className="h-full w-0.5 rounded-full bg-black-200" />
                            )}
                          </div>
                          <div
                            className={clsx(
                              "mt-3 flex grow flex-col",
                              index !== experiences.length - 1 && "mb-3",
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex flex-col">
                                <span className="text-xs-bold">
                                  {job_title}
                                </span>
                                {employment_type && (
                                  <span className="text-button-xxs text-black-500">
                                    {employment_type.name}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col items-end text-button-xxs text-black-500">
                                {start_date && (
                                  <span>
                                    {formatRange(
                                      formatMonthAndYear(start_date),
                                      end_date
                                        ? formatMonthAndYear(end_date)
                                        : "Present",
                                    )}
                                  </span>
                                )}
                                {location && <span>{location.name}</span>}
                              </div>
                            </div>
                            {description && (
                              <ExpandableText>{description}</ExpandableText>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </SectionItem>
                );
              }
              return (
                <SectionItem key={companyName}>
                  <WorkExperienceItem
                    workExperience={experiences[0]}
                    isLoading={false}
                  />
                </SectionItem>
              );
            },
          )}
    </SectionContainer>
  );
}
